// Deutsche Dezimaleingabe (Safari-sicher, ohne <input type="number">):
// "12,50", "12.50", "1.234,56", "1,234.56", "12", "12," → Zahl.

const grouped = (s, sep) => new RegExp(`^\\d{1,3}(\\${sep}\\d{3})+$`).test(s);

// Liefert einen JS-Zahlstring ("1234.56") oder null, wenn die Eingabe ungültig ist.
function normalize(s) {
  const c = s.lastIndexOf(",");
  const d = s.lastIndexOf(".");
  if (c >= 0 && d >= 0) {
    const dec = c > d ? "," : ".";
    const thou = dec === "," ? "." : ",";
    const i = s.lastIndexOf(dec);
    const int = s.slice(0, i);
    const frac = s.slice(i + 1);
    if (!grouped(int, thou) || !/^\d*$/.test(frac)) return null;
    return `${int.split(thou).join("")}.${frac || "0"}`;
  }
  const sep = c >= 0 ? "," : d >= 0 ? "." : null;
  if (!sep) return /^\d+$/.test(s) ? s : null;
  const parts = s.split(sep);
  if (parts.length > 2) return grouped(s, sep) ? parts.join("") : null;
  const [int, frac] = parts;
  // "1.234" = deutsche Tausendertrennung (1234); "0.125" / "12.50" = Dezimalpunkt
  if (sep === "." && frac.length === 3 && /^[1-9]\d{0,2}$/.test(int)) return int + frac;
  if (!/^\d*$/.test(int) || !/^\d*$/.test(frac) || (int === "" && frac === "")) return null;
  return `${int || "0"}.${frac || "0"}`;
}

// opts: integer, decimals (max. angezeigte Nachkommastellen), minDecimals (Standard = decimals)
export function formatDecimal(value, { integer = false, decimals = 2, minDecimals } = {}) {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) return "";
  const max = integer ? 0 : decimals;
  const min = integer ? 0 : Math.min(minDecimals ?? decimals, max);
  return Number(value).toLocaleString("de-DE", { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping: !integer });
}

// → { value: number|null, error: string }  (error = deutscher Text, "" wenn gültig)
export function parseDecimal(input, { integer = false, min = 0, max = Infinity, maxDecimals = 4, required = true, example } = {}) {
  const s = String(input ?? "").trim().replace(/[\s\u00a0€]/g, "");
  if (s === "") return required ? { value: null, error: "Bitte einen Wert eingeben." } : { value: null, error: "" };
  if (s.startsWith("-")) return { value: null, error: "Keine negativen Werte." };
  const norm = /^[0-9.,]+$/.test(s) ? normalize(s) : null;
  if (norm === null) return { value: null, error: `Ungültige Zahl (z. B. ${example || (integer ? "2" : "12,50")}).` };
  const value = Number(norm);
  if (!Number.isFinite(value)) return { value: null, error: "Ungültige Zahl." };
  const fracLen = (norm.split(".")[1] || "").replace(/0+$/, "").length;
  if (integer && fracLen > 0) return { value: null, error: "Bitte eine ganze Zahl eingeben." };
  if (!integer && fracLen > maxDecimals) return { value: null, error: `Höchstens ${maxDecimals} Nachkommastellen.` };
  if (value < min) return { value: null, error: `Mindestens ${formatDecimal(min, { integer, minDecimals: 0, decimals: 4 })}.` };
  if (value > max) return { value: null, error: `Höchstens ${formatDecimal(max, { integer, minDecimals: 0, decimals: 4 })}.` };
  return { value, error: "" };
}

// Alle Feld-Optionen an einer Stelle (parseDecimal + formatDecimal verwenden dieselben Objekte).
export const PRICE_OPTS = { min: 0, max: 1000000, maxDecimals: 4, decimals: 2 };
export const BRUTTO_OPTS = { min: 0, max: 100000, maxDecimals: 2, decimals: 2, example: "6,90" };
export const QTY_OPTS = { integer: true, min: 1, max: 100000 };
export const PERCENT_OPTS = { min: 0, max: 100, maxDecimals: 2, decimals: 2, minDecimals: 0, example: "19" };
export const WEIGHT_OPTS = { min: 0.01, max: 31.5, maxDecimals: 3, decimals: 3, minDecimals: 0, example: "0,8" };
export const intOpts = (min, max, extra = {}) => ({ integer: true, min, max, ...extra });

// Mehrere Textfelder auf einmal prüfen: spec = { feld: opts } → { ok, values, errors }
export function parseFields(obj, spec) {
  const values = {};
  const errors = {};
  Object.entries(spec).forEach(([k, o]) => {
    const r = parseDecimal(obj?.[k], o);
    if (r.error) errors[k] = r.error; else values[k] = r.value;
  });
  return { ok: Object.keys(errors).length === 0, values, errors };
}

// Zahlen aus der API für die Anzeige in Textfeldern formatieren ("0.8" → "0,8").
export function toFieldText(obj, spec) {
  const out = { ...obj };
  Object.entries(spec).forEach(([k, o]) => { out[k] = formatDecimal(obj?.[k], o); });
  return out;
}

export const INVALID_FORM_MSG = "Bitte die rot markierten Felder korrigieren.";
export const RABATT_OPTS = { min: 0, max: 100, maxDecimals: 2, decimals: 2, minDecimals: 0, required: false, example: "12,5" };
